import { useState } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { AlertCircle, Eye, EyeOff } from 'lucide-react';

export const EmailPasswordForm = ({ mode, onSubmit }) => {
  const isRegister = mode === 'register';
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!email.trim() || !password) {
      setError('Please enter your email and password');
      return;
    }

    setSubmitting(true);
    try {
      await onSubmit({ email: email.trim(), password, name: name.trim() });
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const prefix = isRegister ? 'register' : 'login';

  return (
    <form onSubmit={handleSubmit} className="space-y-4" data-testid={`${prefix}-form`}>
      {isRegister && (
        <div className="space-y-1.5">
          <Label htmlFor="auth-name">Farm or your name</Label>
          <Input
            id="auth-name"
            data-testid="register-name-input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Green Acres Farm"
            className="h-12 border-[#D1D1CD]"
          />
        </div>
      )}

      <div className="space-y-1.5">
        <Label htmlFor={`${prefix}-email`}>Email</Label>
        <Input
          id={`${prefix}-email`}
          data-testid={`${prefix}-email-input`}
          type="email"
          autoComplete="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@example.com"
          className="h-12 border-[#D1D1CD]"
        />
      </div>

      <div className="space-y-1.5">
        <Label htmlFor={`${prefix}-password`}>Password</Label>
        <div className="relative">
          <Input
            id={`${prefix}-password`}
            data-testid={`${prefix}-password-input`}
            type={showPassword ? 'text' : 'password'}
            autoComplete={isRegister ? 'new-password' : 'current-password'}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder={isRegister ? 'At least 8 characters' : 'Your password'}
            className="h-12 pr-11 border-[#D1D1CD]"
          />
          <button
            type="button"
            data-testid={`${prefix}-toggle-password`}
            onClick={() => setShowPassword(!showPassword)}
            aria-label={showPassword ? 'Hide password' : 'Show password'}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-[#5C5C5C] hover:text-[#2E4632]"
          >
            {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
          </button>
        </div>
        {isRegister && (
          <p className="text-xs text-[#5C5C5C]">
            Use 8+ characters with at least one letter and one number.
          </p>
        )}
      </div>

      {error && (
        <div
          data-testid={`${prefix}-error`}
          className="flex items-start gap-2 p-3 rounded-md bg-[#BC4749]/10 border border-[#BC4749]/25"
        >
          <AlertCircle className="w-4 h-4 text-[#BC4749] flex-shrink-0 mt-0.5" />
          <p className="text-xs text-[#BC4749] leading-relaxed">{error}</p>
        </div>
      )}

      <Button
        type="submit"
        data-testid={`${prefix}-submit-btn`}
        disabled={submitting}
        className="w-full h-12 bg-[#2E4632] hover:bg-[#1F3122] text-white font-semibold"
      >
        {(() => {
          if (submitting) return isRegister ? 'Creating account...' : 'Signing in...';
          return isRegister ? 'Create account' : 'Sign in';
        })()}
      </Button>
    </form>
  );
};
